import { QueryFunctionContext, QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import useSlingApi from '@sdk/api/useSlingApi';
import { GetMessagePayload } from '@sdk/messages/types/apiTypes';

import {
  CONVERSATION_MESSAGES_CACHE_TIME,
  CONVERSATION_MESSAGES_STALE_TIME,
} from '@web/conversations/consts/cache';
import { LIMIT } from '@web/conversations/consts/messages';
import { useConversationMappers } from '@web/conversations/hooks/useConversationMappers';
import { getConversationMessagesQueryKey } from '@web/conversations/utils/getQueryKey';

import { MessageType } from '../types/MessageType';

export type Params = GetMessagePayload;
type Props = {
  conversationId: ID;
  selectedMessageId: ID | null;
};

export const useGetMessages = ({ conversationId, selectedMessageId }: Props) => {
  const { messages } = useSlingApi();
  const { isReady: isMapperReady, mapBulkAPIMessages } = useConversationMappers();
  const queryKey = getConversationMessagesQueryKey(conversationId, selectedMessageId);

  // @INFO if there's no cutom message ID, the newest page is already loaded
  const hasNewestPageFetched = !selectedMessageId;
  const shouldFetchCustomPage = !!selectedMessageId;
  const defaultParams: Params = shouldFetchCustomPage
    ? {
        afterId: selectedMessageId,
        conversationId,
        include: true,
      }
    : {
        conversationId,
      };

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = defaultParams }: QueryFunctionContext<QueryKey, Params>) => {
      const res = await messages.get(pageParam);

      return mapBulkAPIMessages(res.data);
    },

    cacheTime: CONVERSATION_MESSAGES_CACHE_TIME,
    enabled: isMapperReady,
    // get older messages
    getNextPageParam: (oldestPage) => {
      const oldestMessage = oldestPage[oldestPage.length - 1];
      const isCreationPage = oldestMessage.type === MessageType.CREATION;

      if (isCreationPage) return;

      return {
        beforeId: oldestMessage.id,
        conversationId,
      };
    },
    // get newer messages
    getPreviousPageParam: (newestPage) => {
      if (hasNewestPageFetched) return;

      const newestMessage = newestPage[0];
      const isCreationPage = newestMessage.type === MessageType.CREATION;
      const hasPossiblyNewerMessages = newestPage.length >= LIMIT;

      if (isCreationPage || !hasPossiblyNewerMessages) return;

      return {
        afterId: newestMessage.id,
        conversationId,
      };
    },
    staleTime: CONVERSATION_MESSAGES_STALE_TIME,
  });

  return query;
};
